import { DEFAULT_USER_ID } from "../constants/user";
import type { AppCoordinator } from "../core/AppCoordinator";
import {
	createError,
	ErrorSeverity,
	ErrorType,
	errorHandler,
} from "../error-handling";
import type {
	Comment,
	TranscriptSegment as CoreTranscriptSegment,
	Session,
	SessionSummary,
} from "../types/core";
import type { TranscriptSegment as UITranscriptSegment } from "../ui/components/TranscriptionDisplay";

export interface SessionControlCallbacks {
	onStatusChange: (
		message: string,
		type: "ready" | "running" | "error",
	) => void;
	onTranscriptionUpdate: (segment: UITranscriptSegment) => void;
	onTranscriptionClear: () => void;
	onCommentsUpdate: (comments: Comment[]) => void;
	onSessionStateChange: (isRunning: boolean, isPaused: boolean) => void;
	onSummaryGenerated: (summary: SessionSummary, session: Session) => void;
	onSessionIdChange: (sessionId: string | null) => void;
}

export class SessionControlService {
	constructor(
		private coordinator: AppCoordinator,
		private callbacks: SessionControlCallbacks,
		private checkApiKeys: (isStartup?: boolean) => Promise<boolean>,
	) {}

	async startSession(): Promise<void> {
		if (this.coordinator.isRunning) return;
		if (!(await this.checkApiKeys(false))) return;

		try {
			const session =
				this.coordinator.sessionManager.startSession(DEFAULT_USER_ID);
			this.coordinator.currentSessionId = session.id;
			this.callbacks.onSessionIdChange(session.id);

			this.coordinator.clearTranscription();

			await this.coordinator.commentSystem.initializePreferences(
				this.coordinator.storage,
				DEFAULT_USER_ID,
				this.coordinator.currentSessionId,
			);
			await this.coordinator.audioRecorder.startRecording();
			const stream = this.coordinator.audioRecorder.getStream();
			if (stream) {
				await this.coordinator.audioAnalyzer.initialize(stream);
				this.coordinator.audioAnalyzer.startAnalysis();
			}

			this.coordinator.voiceManager.startListening();
			this.coordinator.isRunning = true;
			this.coordinator.isPaused = false;
			this.callbacks.onSessionStateChange(true, false);
			this.callbacks.onStatusChange("録音中 - 自然に話してください", "running");

			// 自動トリガーをリセットし、生成コールバックを接続する
			this.coordinator.autoCommentTrigger.reset();
			this.coordinator.autoCommentTrigger.onTriggerComment(() => {
				this.generateComments()
					.then(() => {
						this.coordinator.autoCommentTrigger.onCommentGenerated();
					})
					.catch(() => {
						this.coordinator.autoCommentTrigger.onCommentFailed();
					});
			});
		} catch (error) {
			this.callbacks.onStatusChange("セッションの開始に失敗しました", "error");
			errorHandler.handleError(
				createError(
					ErrorType.VOICE_INPUT,
					ErrorSeverity.HIGH,
					`セッションの開始に失敗しました: ${error instanceof Error ? error.message : String(error)}`,
				),
			);
		}
	}

	pauseSession(): void {
		if (!this.coordinator.isRunning || this.coordinator.isPaused) return;
		this.coordinator.voiceManager.stopListening();
		this.coordinator.audioRecorder.pauseRecording();
		if (this.coordinator.currentSessionId) {
			this.coordinator.sessionManager.pauseSession(
				this.coordinator.currentSessionId,
			);
		}
		this.coordinator.isPaused = true;
		this.callbacks.onSessionStateChange(true, true);
		this.callbacks.onStatusChange("一時停止中", "ready");
	}

	resumeSession(): void {
		if (!this.coordinator.isRunning || !this.coordinator.isPaused) return;
		this.coordinator.voiceManager.startListening();
		this.coordinator.audioRecorder.resumeRecording();
		if (this.coordinator.currentSessionId) {
			this.coordinator.sessionManager.resumeSession(
				this.coordinator.currentSessionId,
			);
		}
		this.coordinator.isPaused = false;
		this.callbacks.onSessionStateChange(true, false);
		this.callbacks.onStatusChange("録音中", "running");
	}

	async resumeExistingSession(sessionId: string): Promise<void> {
		if (!(await this.checkApiKeys(false))) return;

		if (this.coordinator.isRunning) {
			// Stop current session if running
			await this.stopSession();
		}

		try {
			await this.coordinator.resumeStoredSession(sessionId);

			const currentSessionId = this.coordinator.currentSessionId;
			if (!currentSessionId) {
				throw new Error("Failed to set currentSessionId during resume");
			}

			await this.coordinator.commentSystem.initializePreferences(
				this.coordinator.storage,
				DEFAULT_USER_ID,
				currentSessionId,
			);

			// Start recording/listening for the resumed session
			await this.coordinator.audioRecorder.startRecording();
			const stream = this.coordinator.audioRecorder.getStream();
			if (stream) {
				await this.coordinator.audioAnalyzer.initialize(stream);
				this.coordinator.audioAnalyzer.startAnalysis();
			}

			this.coordinator.voiceManager.startListening();
			this.coordinator.isRunning = true;
			this.coordinator.isPaused = false;
			this.callbacks.onSessionStateChange(true, false);
			this.callbacks.onStatusChange(
				"セッションを再開しました - 録音中",
				"running",
			);

			// 自動トリガーをリセットし、生成コールバックを接続する
			this.coordinator.autoCommentTrigger.reset();
			this.coordinator.autoCommentTrigger.onTriggerComment(() => {
				this.generateComments()
					.then(() => {
						this.coordinator.autoCommentTrigger.onCommentGenerated();
					})
					.catch(() => {
						this.coordinator.autoCommentTrigger.onCommentFailed();
					});
			});
		} catch (error) {
			this.callbacks.onStatusChange("セッションの再開に失敗しました", "error");
			errorHandler.handleError(
				createError(
					ErrorType.SESSION,
					ErrorSeverity.HIGH,
					`セッションの再開に失敗しました: ${error instanceof Error ? error.message : String(error)}`,
				),
			);
		}
	}

	async stopSession(): Promise<void> {
		if (!this.coordinator.isRunning) return;
		try {
			this.coordinator.voiceManager.stopListening();
			this.coordinator.audioAnalyzer.stopAnalysis();
			const audioFile = await this.coordinator.audioRecorder.stopRecording();
			if (this.coordinator.currentSessionId && audioFile) {
				await this.coordinator.audioManager.saveAudioFile(
					audioFile,
					this.coordinator.currentSessionId,
				);
			}

			if (this.coordinator.currentSessionId) {
				const prefs =
					this.coordinator.preferenceManager.getCurrentPreferences();
				const geminiKey = prefs?.apiKeys?.summaryApiKey;
				const basicPolicy = prefs?.policyConfig?.basicPolicy;

				const summary = await this.coordinator.sessionManager.endSession(
					this.coordinator.currentSessionId,
					geminiKey || undefined,
					basicPolicy,
				);
				const session = await this.coordinator.sessionManager.getSessionById(
					this.coordinator.currentSessionId,
				);
				if (session) {
					this.callbacks.onSummaryGenerated(summary, session);
				}
			}

			this.resetSessionState();
			this.callbacks.onStatusChange("セッション終了", "ready");
		} catch (error) {
			console.error("Failed to stop session cleanly:", error);
			this.resetSessionState();
		}
	}

	async generateComments(): Promise<void> {
		if (!this.coordinator.commentSystem || !this.coordinator.currentSessionId) {
			return;
		}

		const session = await this.coordinator.sessionManager.getSessionById(
			this.coordinator.currentSessionId,
		);
		if (session) {
			// Performance optimization: Collect recent transcript via backward iteration
			// to avoid O(N) mapping and joining of the entire transcript array.
			// This turns an O(N) operation into an average O(1) proportional to the 2000-char limit.
			let recentTranscript = "";
			const transcript = session.transcript;
			for (let i = transcript.length - 1; i >= 0; i--) {
				const text = transcript[i].text;
				recentTranscript =
					text + (recentTranscript ? `\n${recentTranscript}` : "");
				if (recentTranscript.length >= 2000) {
					recentTranscript = recentTranscript.slice(-2000);
					break;
				}
			}

			await this.coordinator.commentSystem.generateComments({
				recentTranscript,
				silenceDuration: 0,
			});
		}
	}

	handleCommentClick(id: string): void {
		this.coordinator.commentSystem.onInteraction?.({
			commentId: id,
			type: "pin",
			timestamp: new Date(),
			confidence: 1.0,
		});
	}

	async updateTranscriptSegment(
		sessionId: string,
		segmentId: string,
		newText: string,
	): Promise<void> {
		await this.coordinator.sessionManager.updateTranscriptSegment(
			sessionId,
			segmentId,
			newText,
		);
	}

	async deleteTranscriptSegment(
		sessionId: string,
		segmentId: string,
	): Promise<void> {
		await this.coordinator.sessionManager.removeTranscriptSegment(
			sessionId,
			segmentId,
		);
	}

	async updateSessionTranscript(
		sessionId: string,
		transcript: CoreTranscriptSegment[],
	): Promise<void> {
		await this.coordinator.sessionManager.updateSessionTranscript(
			sessionId,
			transcript,
		);
	}

	async generateSummaryManual(sessionId: string): Promise<SessionSummary> {
		const session =
			await this.coordinator.sessionManager.getSessionById(sessionId);
		if (!session) throw new Error("Session not found");

		const prefs = this.coordinator.preferenceManager.getCurrentPreferences();
		const geminiKey = prefs?.apiKeys?.summaryApiKey;
		const basicPolicy = prefs?.policyConfig?.basicPolicy;

		const summary = await this.coordinator.sessionManager.generateSummary(
			session,
			geminiKey || undefined,
			basicPolicy,
		);
		await this.coordinator.storage.saveSummary(summary);
		return summary;
	}

	private resetSessionState(): void {
		this.coordinator.isRunning = false;
		this.coordinator.isPaused = false;
		this.coordinator.currentSessionId = null;
		this.callbacks.onSessionIdChange(null);
		this.callbacks.onSessionStateChange(false, false);
		this.coordinator.commentSystem.clearComments();
		this.coordinator.clearTranscription();
		// セッション終了時に自動トリガーをリセット
		this.coordinator.autoCommentTrigger.reset();
	}
}
